import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useOpenEditLink } from '@/context/edit-link/edit-link-context';
import { useReferenceMap } from '@/context/references/references-context';
import { useReferenceRowLookup } from '@/context/references/reference-row-index-context';
import { useSelectionToolbarApi } from '@/context/selection-toolbar/selection-toolbar-context';
import { useAppSelector } from '@/context/store/hooks';
import { updateNode } from '@/context/store/slices/documentDataSlice';
import {
  resolveRefMeta,
  sanitizeHtml,
  serializeHtmlToText,
  writeRenderedTextToNode,
} from '@/lib/edit-text-serialize';
import { resolveEditLinkOpenFromRange } from '@/lib/resolve-edit-link-open';
import { registerEditorFlush, setEditorFlushDirty } from '@/lib/editor-flush-registry';

import './editable-text.scss';

type EditableTextProps = {
  nodeId: string;
  text: string;
  className?: string;
  placeholder?: string;
};

const DEBOUNCE_MS = 300;
const REF_TOKEN_ID_RE = /\{\{ref:([^}]+)\}\}/g;

// True when `text` contains ref tokens that don't yet have a matching chip in
// the live DOM — e.g. right after the EditLink modal writes a new token to
// Redux while this node is still focused. Used to force a resync even though
// the focus guard would normally skip it.
function hasRefTokenDivergence(node: HTMLElement, text: string): boolean {
  const tokenIds = new Set(
    Array.from(text.matchAll(REF_TOKEN_ID_RE), match => match[1]),
  );
  if (tokenIds.size === 0) return false;
  const chipIds = new Set(
    Array.from(
      node.querySelectorAll<HTMLElement>('.ref-chip[data-ref-id]'),
      chip => chip.getAttribute('data-ref-id') ?? '',
    ),
  );
  return Array.from(tokenIds).some(id => !chipIds.has(id));
}

function EditableText({
  nodeId,
  text,
  className,
  placeholder,
}: EditableTextProps) {
  const dispatch = useDispatch();
  const refMap = useReferenceMap();
  const { getReferences } = useReferenceRowLookup();
  const { registerSelectionHost } = useSelectionToolbarApi();
  const openEditLink = useOpenEditLink();
  const docNode = useAppSelector(s => s.documentNode.data);
  const divRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textRef = useRef(text);

  useEffect(() => {
    textRef.current = text;
  }, [text]);

  // Shared by flushChanges and onAddReference so both read the same
  // serialize+sanitize snapshot of the live DOM.
  const readSerializedText = useCallback((node: HTMLElement): string => {
    const serialized = serializeHtmlToText(node);
    return sanitizeHtml(serialized);
  }, []);

  const flushChanges = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const node = divRef.current;
    if (!node) {
      setEditorFlushDirty(nodeId, false);
      return;
    }
    const cleaned = readSerializedText(node);
    if (cleaned === textRef.current) {
      setEditorFlushDirty(nodeId, false);
      return;
    }
    // Guard against clobbering Redux with a DOM snapshot that dropped ref
    // tokens the current text still carries (e.g. a chip not yet
    // materialized in the DOM). Losing a token here orphans its reference row.
    const priorTokenIds = new Set(
      Array.from(textRef.current.matchAll(REF_TOKEN_ID_RE), m => m[1]),
    );
    if (priorTokenIds.size > 0) {
      const cleanedTokenIds = new Set(
        Array.from(cleaned.matchAll(REF_TOKEN_ID_RE), m => m[1]),
      );
      const droppedToken = Array.from(priorTokenIds).some(
        id => !cleanedTokenIds.has(id),
      );
      if (droppedToken) return;
    }
    setEditorFlushDirty(nodeId, false);
    dispatch(
      updateNode({
        id: nodeId,
        changes: { text: cleaned },
      }),
    );
  }, [dispatch, nodeId, readSerializedText]);

  const onAddReference = useCallback(
    (range: Range) => {
      const host = divRef.current;
      if (!host) return;
      // Flush DOM -> Redux first so the modal's live-DOM selection offsets
      // and the source text it splices into stay aligned.
      flushChanges();
      // Read lazily at click time — not subscribed to during render — so
      // this callback stays stable across references-query refetches and
      // doesn't force a re-render on every keystroke elsewhere in the doc.
      const result = resolveEditLinkOpenFromRange(range, new Map(), {
        refs: getReferences(),
        getSourceText: () => textRef.current,
        meta: {
          nodeId: docNode.id,
          bookId: docNode.bookId,
          language: docNode.language,
        },
      });
      if (!result) return;
      openEditLink(result.sourceRef, result.mode);
    },
    [
      flushChanges,
      openEditLink,
      docNode.id,
      docNode.bookId,
      docNode.language,
      getReferences,
    ],
  );

  useEffect(() => {
    return registerEditorFlush(nodeId, {
      flush: async () => {
        flushChanges();
      },
      cancel: () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        setEditorFlushDirty(nodeId, false);
      },
      isBusy: () => timerRef.current !== null,
    });
  }, [nodeId, flushChanges]);

  useEffect(() => {
    const node = divRef.current;
    if (!node) return;
    return registerSelectionHost({
      id: nodeId,
      element: node,
      onAddReference,
    });
  }, [nodeId, registerSelectionHost, onAddReference]);

  useEffect(() => {
    const node = divRef.current;
    if (!node) return;
    // Don't clobber the DOM while the user is typing — browser owns the
    // contenteditable mid-keystroke. EditableCell pattern. Initial mount
    // passes this guard because activeElement is the body, not us. Exception:
    // if `text` now carries a ref token with no matching chip in the DOM
    // (e.g. the EditLink modal just wrote a new token to Redux while this
    // node kept focus), force the resync so the chip appears immediately.
    if (
      document.activeElement === node &&
      !hasRefTokenDivergence(node, text)
    ) {
      return;
    }
    writeRenderedTextToNode(node, text, refMap, nodeId);
  }, [nodeId, text, refMap]);

  // Refresh chip labels in-place when refMap arrives later than the initial
  // render. The init effect above captures refMap at mount; if the references
  // query hadn't resolved yet, every chip rendered as data-broken="true" with
  // its raw refId as text. Rewriting the whole node here would clobber the
  // caret — mutate only the chip elements so typing isn't disrupted.
  useEffect(() => {
    const node = divRef.current;
    if (!node) return;
    const chips = node.querySelectorAll<HTMLElement>('.ref-chip[data-ref-id]');
    chips.forEach(chip => {
      const id = chip.getAttribute('data-ref-id') ?? '';
      const meta = resolveRefMeta(refMap, id, nodeId);
      if (meta) {
        chip.textContent = meta.name;
        chip.removeAttribute('data-broken');
        if (meta.lost) {
          chip.setAttribute('data-lost', 'true');
        } else {
          chip.removeAttribute('data-lost');
        }
      } else {
        chip.setAttribute('data-broken', 'true');
        chip.removeAttribute('data-lost');
        chip.textContent = id;
      }
    });
  }, [refMap]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  function handleInput(): void {
    const node = divRef.current;
    if (!node) return;
    setEditorFlushDirty(nodeId, true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      flushChanges();
    }, DEBOUNCE_MS);
  }

  function handleBlur(): void {
    flushChanges();
  }

  // The browser's default Enter behavior in a contenteditable wraps the new
  // line in an unstyled <div>, which doesn't carry this element's class and
  // so can't reach its --font-family (etc.) CSS vars. Insert a deterministic
  // <br> within the same element instead — mirrors
  // DocumentEditorParagraphCluster's identical workaround for EditorJS blocks.
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    document.execCommand('insertLineBreak');
  }

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>): void {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const plain = e.clipboardData.getData('text/plain');
    const cleaned = sanitizeHtml(html || plain);
    document.execCommand('insertHTML', false, cleaned);
  }

  return (
    <>
      <div
        ref={divRef}
        className={
          className ? `editable-text ${className}` : 'editable-text'
        }
        contentEditable
        suppressContentEditableWarning
        data-id={nodeId}
        data-placeholder={placeholder}
        onInput={handleInput}
        onBlur={handleBlur}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
      />
    </>
  );
}

export { EditableText };
